export interface PlayerAccessRights {
  mouse?: boolean;
  keyboard?: boolean;
  touch?: boolean;
  gamepad?: boolean;
}

let logs = [] as string[];
let event = null as Event | null;
let access: PlayerAccessRights = {
  mouse: false,
  keyboard: false,
  touch: false,
  gamepad: false,
};

// Tracks whether the authoritative host `setInputAccess` grant has been applied
// for the current session. Consumers (e.g. the marketing-demo quickstart
// coordinator) use this to decide whether to fall back to a campaign-derived
// access when the host grant is dropped/delayed, without confusing their own
// baseline/fallback writes for a real host grant (#13862).
let hostAccessReceived = false;

const subs = {
  logs: [] as ((events: string[]) => void)[],
  event: [] as ((event: Event) => void)[],
  access: [] as ((access: PlayerAccessRights) => void)[],
};

const emitEventChange = () => {
  if (!event) return;
  for (const s of subs.event) {
    s(event);
  }
};

const emitLogsChange = () => {
  for (const s of subs.logs) {
    s(logs);
  }
};

const emitAccessChange = () => {
  for (const s of subs.access) {
    s(access);
  }
};

export const captureStore = {
  addEvent(e: Event) {
    logs = [e.type, ...logs];
    if (logs.length > 10) {
      logs.pop();
    }
    event = e;
    emitLogsChange();
    emitEventChange();
  },
  updateAccess(newAccess: PlayerAccessRights) {
    if (
      access.mouse !== newAccess.mouse ||
      access.keyboard !== newAccess.keyboard ||
      access.touch !== newAccess.touch ||
      access.gamepad !== newAccess.gamepad
    ) {
      access = { ...newAccess };
      emitAccessChange();
    }
  },
  /**
   * Apply the authoritative host `setInputAccess` grant. Identical to
   * `updateAccess` but additionally records that a real host grant has arrived
   * for this session, so consumers can suppress any client-side fallback.
   */
  applyHostAccess(newAccess: PlayerAccessRights) {
    hostAccessReceived = true;
    this.updateAccess(newAccess);
  },
  /** Whether a host `setInputAccess` grant has been applied this session. */
  hasHostAccess() {
    return hostAccessReceived;
  },
  /**
   * Reset the host-grant flag. Call when a new session begins (e.g. coordinator
   * construction) so a prior session's grant does not suppress the new
   * session's fallback.
   */
  resetHostAccess() {
    hostAccessReceived = false;
  },
  subscribeEvent(callback: (event: Event) => void) {
    subs.event = [...subs.event, callback];
    return () => {
      subs.event = subs.event.filter((s) => s !== callback);
    };
  },
  subscribeLogs(callback: (events: string[]) => void) {
    subs.logs = [...subs.logs, callback];
    return () => {
      subs.logs = subs.logs.filter((s) => s !== callback);
    };
  },
  subscribeAccess(callback: (access: PlayerAccessRights) => void) {
    subs.access = [...subs.access, callback];
    return () => {
      subs.access = subs.access.filter((s) => s !== callback);
    };
  },
  getEvent() {
    return event;
  },
  getLogs() {
    return logs;
  },
  getAccess() {
    return access;
  },
};
